/**
 * Authentication store over Better Auth (the shared authClient). Impersonation
 * is the exception: frontend-only against the boards demo store, no server call.
 *
 * Persisted to localStorage as a hint for instant UI. The server session cookie
 * is the source of truth; protected navigation awaits validateSession().
 */
import {authClient} from '@shared_ui/auth_client';
import type {IAuthTypedFlowError} from '@shared_ui/auth_errors';
import {asAuthErrorCode, authErrorMessage, isAuthError} from '@shared_ui/auth_errors';
import {resetTrpcClient} from '@shared_ui/trpc_client';
import type {
   IAuthUser,
   SessionsLookup,
   UsernameAvailability,
   VerifyEmailOutcome,
} from '@shared/auth_types';
import {LoginMethod, toUserRole, UserRole} from '@shared/auth_types';
import {gDialogService} from '@src/plugins/dialog_service/dialog_service';
import {useDemoBoardsStore} from '@src/store/demo_data/demo_boards_store';
import {defineStore} from 'pinia';
import {AnalyticsEvent} from '@shared/analytics_events';
import {captureEvent, identifyUser, resetIdentity} from '@src/plugins/analytics';

/** True once the persisted user has been checked against the server session
 * this page load. Module-level so it is never persisted. */
let session_validated_this_load = false;

export interface IAuthState {
   /** Signed-in user (persisted hint; server session cookie is the truth). */
   user: IAuthUser | null;

   /** Demo only: boards demo store member id of the admin who started
    * impersonation; null when not impersonating. */
   impersonatorUserId: string | null;

   /** Method used for the most recent successful sign-in. */
   lastUsedLoginMethod: LoginMethod | null;

   /** New email awaiting its verification link (UI hint; the server
    * applies the change only when the link is clicked). */
   pendingEmailChange: string | null;
}

export const useAuthStore = defineStore('auth', {
   state: (): IAuthState => ({
      user: null,
      impersonatorUserId: null,
      lastUsedLoginMethod: null,
      pendingEmailChange: null,
   }),

   getters: {
      isAuthenticated(state): boolean {
         return state.user != null;
      },

      signedInUserId(state): string | null {
         return state.user?.id ?? null;
      },

      isImpersonating(state): boolean {
         return state.impersonatorUserId != null;
      },

      /** True when the user's system-wide role is admin (not an organization role). */
      isSystemAdmin(state): boolean {
         return state.user?.role === UserRole.ADMIN;
      },
   },

   actions: {
      // ----------- [ Session Checking ] ----------- //
      /**
       * Refresh the user from the server session without awaiting it. No-op
       * when signed out. Not an access check; use validateSession() for that.
       */
      syncSessionInBackground(): void {
         if (this.user == null) {
            return;
         }
         void this.refreshSession();
      },

      /**
       * Refresh the user from the server session and wait for it. With
       * checkOnce, only the first call per page load waits and later calls
       * refresh in the background.
       */
      async validateSession(opts?: {checkOnce: boolean}): Promise<void> {
         if (opts?.checkOnce === true && session_validated_this_load) {
            this.syncSessionInBackground();
            return;
         }
         session_validated_this_load = true;
         await this.refreshSession();
      },

      /** Replace `user` with the server session's user, or clear it when there is none. */
      async refreshSession(): Promise<void> {
         const result = await authClient.getSession();

         // A transport or server error keeps the current user; a missing
         // session arrives as success with null data.
         if (result.error != null) {
            return;
         }

         if (result.data == null) {
            if (this.user != null) {
               resetIdentity();
            }
            this.user = null;
            this.impersonatorUserId = null;
            return;
         }

         this.user = toAuthUser(result.data.user);
         identifyUser(this.user);
      },

      /** End the real session and clear local auth state. */
      async signOut(): Promise<void> {
         await authClient.signOut();
         await resetTrpcClient();
         resetIdentity();
         this.$reset();
      },

      // ----------- [ Email Based Sign-in / mgmt ] ----------- //

      /** Sign in with an email or username. Returns an error message or null. */
      async signIn(identifier: string, password: string): Promise<string | null> {
         const trimmed = identifier.trim();

         const result = trimmed.includes('@')
            ? await authClient.signIn.email({email: trimmed, password})
            : await authClient.signIn.username({username: trimmed, password});

         if (result.error != null) {
            // Codes get friendly overrides; anything else keeps the server
            // message (e.g. a banned account's reason).
            return authErrorMessage(result.error, 'Sign-in failed');
         }
         if (result.data?.user == null) {
            return 'Sign-in failed';
         }

         session_validated_this_load = true;
         this.user = toAuthUser(result.data.user);
         identifyUser(this.user);
         captureEvent(AnalyticsEvent.USER_SIGN_IN);
         this.impersonatorUserId = null;
         this.syncLastUsedLoginMethod();

         // Re-authenticate the WS transport with the new session cookie.
         await resetTrpcClient();
         return null;
      },

      /** Create an account and sign it in. Returns an error message or null. */
      async signUp(opts: {
         name: string;
         email: string;
         displayUsername: string;
         password: string;
      }): Promise<string | null> {
         const result = await authClient.signUp.email({
            name: opts.name.trim(),
            email: opts.email.trim(),
            password: opts.password,
            // The username plugin normalizes this into the unique handle and
            // keeps the original casing as displayUsername.
            username: opts.displayUsername.trim(),
         });

         if (result.error != null) {
            return authErrorMessage(result.error, 'Sign-up failed');
         }
         if (result.data?.user == null) {
            return 'Sign-up failed';
         }

         session_validated_this_load = true;
         this.user = toAuthUser(result.data.user);
         identifyUser(this.user);
         captureEvent(AnalyticsEvent.USER_SIGN_UP);
         this.impersonatorUserId = null;
         this.syncLastUsedLoginMethod();

         await resetTrpcClient();
         return null;
      },

      /** Check whether a username is free. 'invalid' means the server rejected its format. */
      async checkUsernameAvailable(username: string): Promise<UsernameAvailability> {
         const result = await authClient.isUsernameAvailable({username});

         if (result.error != null) {
            return 'invalid';
         }
         return result.data?.available === true ? 'available' : 'taken';
      },

      // ----------- [ Account Recovery and Security ] ----------- //
      /**
       * Email a password-reset link that lands on `redirect_url` with ?token=
       * (or ?error=INVALID_TOKEN). Success never reveals whether the email
       * exists; a non-null return is a transport or server failure.
       */
      async requestPasswordReset(email: string, redirect_url: string): Promise<string | null> {
         const result = await authClient.requestPasswordReset({
            email: email.trim(),
            redirectTo: redirect_url,
         });

         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not send the reset email');
         }
         return null;
      },

      /**
       * Set a new password using the token from the reset link. An expired,
       * malformed or already-used token comes back with code 'INVALID_TOKEN'.
       */
      async resetPassword(
         new_password: string,
         token: string,
      ): Promise<IAuthTypedFlowError | null> {
         const result = await authClient.resetPassword({newPassword: new_password, token});

         if (result.error != null) {
            return {
               code: asAuthErrorCode(result.error.code),
               message: authErrorMessage(result.error, 'Could not reset the password'),
            };
         }
         return null;
      },

      /**
       * Verify the email address with the token from the verification link.
       * On success a signed-in user is refreshed from the server.
       */
      async verifyEmail(token: string): Promise<VerifyEmailOutcome> {
         const result = await authClient.verifyEmail({query: {token}});

         if (result.error != null) {
            return isAuthError(result.error, 'TOKEN_EXPIRED') ? 'expired' : 'invalid';
         }

         if (this.user != null) {
            await this.refreshSession();
         }
         return 'verified';
      },

      /** Resend the verification email. Returns an error message or null. */
      async resendVerificationEmail(email: string): Promise<string | null> {
         const result = await authClient.sendVerificationEmail({email: email.trim()});

         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not send the verification email');
         }
         return null;
      },

      // ----------- [ Account Management ] ----------- //

      /**
       * Update profile fields, then refresh the user from the server. Pass only
       * the fields that changed; a passed username is re-validated for format
       * and uniqueness (the caller's own current username is allowed).
       */
      async updateProfile(opts: {
         name?: string;
         username?: string;
         image?: string | null;
      }): Promise<string | null> {
         const result = await authClient.updateUser({
            ...(opts.name != null ? {name: opts.name.trim()} : {}),
            // update-user does not derive displayUsername from username
            // (Better Auth 1.6.23), so both are sent.
            ...(opts.username != null
               ? {username: opts.username.trim(), displayUsername: opts.username.trim()}
               : {}),
            ...(opts.image !== undefined ? {image: opts.image} : {}),
         });

         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not update the profile');
         }

         await this.refreshSession();
         return null;
      },

      /**
       * Change the password. With revoke_other_sessions the server replaces
       * every session, including this browser's, so the tRPC client is reset.
       */
      async changePassword(
         current_password: string,
         new_password: string,
         revoke_other_sessions: boolean,
      ): Promise<string | null> {
         const result = await authClient.changePassword({
            currentPassword: current_password,
            newPassword: new_password,
            revokeOtherSessions: revoke_other_sessions,
         });

         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not change the password');
         }

         if (revoke_other_sessions) {
            await resetTrpcClient();
         }
         return null;
      },

      /** Map the last-login-method plugin's cookie value into state. */
      syncLastUsedLoginMethod(): void {
         const method = authClient.getLastUsedLoginMethod();
         if (method == null) {
            this.lastUsedLoginMethod = null;
            return;
         }
         this.lastUsedLoginMethod =
            method === LoginMethod.USERNAME ? LoginMethod.USERNAME : LoginMethod.EMAIL;
      },

      // ------ [ Session Management  ] ------ //
      /** List this account's signed-in sessions. */
      async loadSessions(): Promise<SessionsLookup> {
         const [list_result, session_result] = await Promise.all([
            authClient.listSessions(),
            authClient.getSession(),
         ]);

         if (list_result.error != null) {
            return {
               kind: 'error',
               message: authErrorMessage(list_result.error, 'Could not load sessions'),
            };
         }

         const current_token = session_result.data?.session.token ?? null;
         const sessions = (list_result.data ?? []).map((s) => ({
            id: s.id,
            token: s.token,
            ipAddress: s.ipAddress ?? null,
            userAgent: s.userAgent ?? null,
            createdAt: String(s.createdAt),
            updatedAt: String(s.updatedAt),
            isCurrent: s.token === current_token,
         }));
         return {kind: 'ok', sessions};
      },

      /** Revoke one session by its token. Returns an error message or null. */
      async revokeSession(token: string): Promise<string | null> {
         const result = await authClient.revokeSession({token});
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not revoke the session');
         }
         return null;
      },

      /** Sign out every session except this browser's. */
      async revokeOtherSessions(): Promise<string | null> {
         const result = await authClient.revokeOtherSessions();
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not revoke the sessions');
         }
         return null;
      },

      /**
       * Email a verification link to the new address; the change applies only
       * when that link is opened. Success does not mean the address is free:
       * the server answers the same when it is already taken.
       */
      async requestEmailChange(new_email: string): Promise<string | null> {
         const result = await authClient.changeEmail({newEmail: new_email.trim()});
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not start the email change');
         }

         this.pendingEmailChange = new_email.trim().toLowerCase();
         return null;
      },

      /**
       * Email an account-deletion confirmation link. Deletion happens when the
       * link is opened in this signed-in browser, which then lands on sign-in
       * with a goodbye notice.
       */
      async requestAccountDeletion(): Promise<string | null> {
         const result = await authClient.deleteUser({
            callbackURL: `${globalThis.location.origin}/auth/sign-in?accountDeleted=1`,
         });
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not start account deletion');
         }
         return null;
      },

      // Everything below is frontend-only against the boards demo store; no server call.

      // ------- [ Impersonation ] ------- //

      /**
       * Make the target the demo store's current member and remember who
       * started it; the real session is untouched. Returns an error message or null.
       */
      startImpersonation(member_id: string): string | null {
         const demo_boards_store = useDemoBoardsStore();
         if (!demo_boards_store.currentUser.isAppAdmin) {
            return 'Only app admins can impersonate users';
         }
         const target = demo_boards_store.memberById(member_id);
         if (target == null) {
            return 'User not found';
         }
         if (target.id === demo_boards_store.currentUserId) {
            return 'You are already signed in as this user';
         }

         this.impersonatorUserId = demo_boards_store.currentUserId;
         demo_boards_store.currentUserId = target.id;
         return null;
      },

      /** Restore the demo member who started impersonation; no-op when not impersonating. */
      stopImpersonation(): void {
         if (this.impersonatorUserId == null) {
            return;
         }
         useDemoBoardsStore().currentUserId = this.impersonatorUserId;
         this.impersonatorUserId = null;
         gDialogService.showSnackbar({message: 'Back to your own account', type: 'info'});
      },
   },

   persist: true,
});

/** Map an authClient response user onto the store's identity shape. */
function toAuthUser(user: {
   id: string;
   name: string;
   email: string;
   emailVerified: boolean;
   username?: string | null;
   displayUsername?: string | null;
   image?: string | null;
   role?: string | null;
}): IAuthUser {
   return {
      id: user.id,
      name: user.name,
      email: user.email,
      emailVerified: user.emailVerified,
      username: user.username ?? null,
      displayUsername: user.displayUsername ?? null,
      image: user.image ?? null,
      role: toUserRole(user.role),
   };
}
